import { useState, useEffect, useRef } from 'react'
import { Formik, Form } from 'formik'
import { Button, Row, Col, Card, ListGroup, ListGroupItem, CardFooter } from 'reactstrap'
import { Drawer, LoaderButton } from 'components'
import PropTypes from 'prop-types'
import { VoucherCodeGenerateForm } from 'domains/voucher/organisms'
import { VoucherService } from 'domains/voucher/services'
import CustomerVoucherRulesApiV2 from 'api/v2/admins/customer-voucher-rules-api-v2'
import ShipmentMethodsApiV2 from 'api/v2/admins/shipment-methods-api-v2'
import ConditionFields from './condition-fields'
import VoucherRuleShipmentFields from './voucher-rule-shipment-fields'
import VoucherRuleItemFields from './voucher-rule-item-fields'
import voucherSchema from './voucher-rule-form-schema'
import VoucherRuleGeneralFields from './voucher-rule-general-rule-fields'
import { initialValues, non_marketing_discount_rule } from './voucher-form-initial-values'
import VoucherAttributesFieldArray from './attributes-field-array'
import { ToggleField } from 'components/molecules'
import { RuleDescriptions } from 'domains/voucher/'

const propTypes = {
  voucherRule : PropTypes.object,
  onSubmit    : PropTypes.func,
  pageUtils   : PropTypes.object
}

const VoucherForm = ({
  voucherRule = {},
  onSubmit,
  pageUtils
}) => {

  const [ shipmentMethods,   setShipmentMethods   ]         = useState([])
  const [ productRuleAttributes, setProductRuleAttributes ] = useState([])
  const [ cartRuleAttributes, setCartRuleAttributes ]       = useState([])

  useEffect(() => {
    CustomerVoucherRulesApiV2.attributes()
      .then((response) => {
        setProductRuleAttributes(response.data.product_attributes)
        setCartRuleAttributes(response.data.cart_attributes)
      })
      .catch((error) => {
        // pageUtils.setApiErrorMsg(error.response.data)
        alert(JSON.stringify(error.response.data))
      })

    ShipmentMethodsApiV2.get()
      .then((response) => {
        setShipmentMethods(response.data.shipment_methods)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ pageUtils ])

  /**
   * Generate Voucher Code Drawer
   *  - Voucher Code Single Form
   *  - Voucher Code Generate Form
   */
  const [ isDrawerOpen, setIsDrawerOpen ] = useState(false)

  /**
   * Reference to formik form so generate code form can set values
   */
  const voucherFormRef               = useRef(null)
  const handleSubmitGenerateCodeForm = async (values, formik) => {
    const {
      usePerCustomer,
      usageLimit,
      quantity,
      codeLength,
      codeFormat,
      codePrefix,
      codeSuffix,
      dashCount,
      from_date,
      to_date
    } = values

    let voucherAttributes = []
    for (let i = 0; i < quantity; i++) {
      const generatedCode = VoucherService.generateCode(
        codeLength,
        codeFormat,
        codePrefix,
        codeSuffix,
        dashCount
      )
      const data          = {
        code             : generatedCode,
        use_per_customer : usePerCustomer,
        usage_limit      : usageLimit,
        from_date        : from_date,
        to_date          : to_date
      }
      voucherAttributes.push(data)
    }
    const newVoucherAttributes = [
      ...voucherFormRef.current.values.vouchers_attributes,
      ...voucherAttributes
    ]
    voucherFormRef.current.setFieldValue('vouchers_attributes', newVoucherAttributes)
    formik.reset()
    setIsDrawerOpen(false)
  }

  const isSubmitDisabled = (formik) => (
    formik.isSubmitting ||
      !formik.dirty
  )

  const handleChangeNonMarketingDiscount = (formik) => {
    const non_marketing_discount = formik.values.non_marketing_discount
    const product_rules          = formik.values.product_rules
    if (!non_marketing_discount) {
      product_rules.push(non_marketing_discount_rule)
    }
    else {
      formik.values.product_rules = product_rules.filter((rule) => rule.code != non_marketing_discount_rule.code)
    }

    return !non_marketing_discount
  }
  return (
    <>
      <Formik
        innerRef={ voucherFormRef }
        enableReinitialize={ true }
        initialValues={ initialValues(voucherRule) }
        validationSchema={ voucherSchema }
        onSubmit={ onSubmit }
      >
        {
          (formik) => (
            <Form>
              <Row>
                <Col xs='12'>
                  <h2 className='mb-0'>Main Details</h2>
                  <p>{ 'Set the general attributes of the voucher you are creating.' }</p>
                  <Card body>
                    <VoucherRuleGeneralFields />
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md='6'>
                  <h2>{ 'Item Discount Rules' }</h2>
                  <p>{ 'Applied only to the prices of the cart items' }</p>
                  <Card body>
                    <VoucherRuleItemFields />
                  </Card>
                </Col>
                <Col md='6'>
                  <h2>{ 'Shipment Discount Rules' }</h2>
                  <p>{ 'Applied only to the shipment cost of the order' }</p>
                  <Card body>
                    <VoucherRuleShipmentFields />
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md='12'>
                  <h2>{ 'Cart Rules' }</h2>
                  <p>{ 'Item & Shipment Discounts (above) will be applied if the Cart Items meet the conditions that you set below.' }</p>
                  <ul>
                    <li>Voucher will be applied if <strong>no rules</strong> are defined.</li>
                  </ul>
                </Col>
              </Row>

              <Row>
                <Col xs={ 12 } md={ 6 }>
                  <ListGroup className='card'>
                    <ListGroupItem className='font-weight-bolder border-left-0 border-right-0'>Product Rules</ListGroupItem>
                    <RuleDescriptions
                      label='Product Rule Descriptions'
                      rules = { voucherRule.product_rule_descriptions }
                    />
                    <div className='px-3 pt-3'>
                      <ToggleField
                        label={ 'Cannot Use on Marketing Discount Products' }
                        name='non_marketing_discount'
                        onClick={ () => handleChangeNonMarketingDiscount(formik) }
                        formText={ <>
                          <strong>Yes: </strong>Voucher tidak bisa dipakai untuk product yang di marketing discount.
                          <br />
                          <strong>No: </strong>Voucher bisa dipakai untuk product yang di marketing discount.
                        </> }
                      />
                    </div>
                    <ConditionFields
                      name='product_rules'
                      values={ formik.values.product_rules }
                      attributes={ productRuleAttributes }
                    />
                  </ListGroup>
                </Col>

                <Col xs={ 12 } md={ 6 }>
                  <ListGroup className='card'>
                    <ListGroupItem className='font-weight-bolder border-left-0 border-right-0'>Cart Rules</ListGroupItem>
                    <RuleDescriptions
                      label='Cart Rule Descriptions'
                      rules = { voucherRule.cart_rule_descriptions }
                    />
                    <ConditionFields
                      name='cart_rules'
                      values={ formik.values.cart_rules }
                      attributes={ cartRuleAttributes }
                      shipmentMethods={ shipmentMethods }
                    />
                  </ListGroup>
                </Col>
              </Row>

              <Row>
                <Col>
                  <h2>{ 'Voucher Codes' }</h2>
                  <p>{ 'A Voucher Rule can have one or many Voucher Codes.' }</p>
                  <ul>
                    <li>{ 'Every code will use the same set of rules defined above.' }</li>
                    <li>{ 'Use the Code Generator if you need to distribute many unique codes' }</li>
                    <li>Leave <strong>Use Per Customer & Usage Limit</strong> blank if coupon has unlimited use.</li>
                    <li>Columns <strong>From Date</strong> & <strong>To Date</strong> can be left blank and will use valid date base on voucher rule above.</li>
                  </ul>
                  <Card>
                    <VoucherAttributesFieldArray
                      name={ 'vouchers_attributes' }
                    />
                    <CardFooter>

                      <Button
                        className='float-right'
                        outline
                        color={ 'primary' }
                        onClick={ () => { setIsDrawerOpen(true) } }
                      >
                        { 'Code Generator' }
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>

              <div className='text-right'>
                <LoaderButton
                  size='lg'
                  type='submit'
                  color='primary'
                  isLoading={ formik.isSubmitting }
                  disabled={ isSubmitDisabled(formik) }
                >
                  { 'Save Voucher' }
                </LoaderButton>
                {
                  isSubmitDisabled(formik) &&
                    <div className='text-right small text-muted pt-1'>{ 'There are no changes and/or errors exists.' }</div>
                }
              </div>
            </Form>
          )
        }
      </Formik>
      <Drawer
        isOpen={ isDrawerOpen }
        title={ 'Generate Voucher Codes' }
        onClickClose={ () => setIsDrawerOpen(false) }
      >
        <div className='p-3'>
          <p>Automagically generate random voucher codes.</p>
          <VoucherCodeGenerateForm
            onSubmit={ handleSubmitGenerateCodeForm }
          />
        </div>
      </Drawer>
    </>
  )
}
VoucherForm.propTypes = propTypes

export default VoucherForm
